import React, { useState } from 'react';
import { Tabs, Table, Button, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { LoginOutlined, LogoutOutlined, PhoneOutlined, SearchOutlined } from '@ant-design/icons';

// Cartões carregados de cada vez no telemóvel ("Todas" pode ter mais de mil reservas)
const CARTOES_POR_PAGINA = 30;

const nomeModelo = (modelo) => (modelo !== 'a:0:{}' ? modelo : '');

// dd-mm-aaaa -> dd/mm
const diaCurto = (data) => (data ? data.slice(0, 5).replace('-', '/') : '');

const EstadoReserva = ({ estado }) => (
    <span className={`estado-chip estado-chip-${(estado || '').toLowerCase()}`}>{estado}</span>
);

const CartaoReserva = ({ reserva, destaque, smoothScrollToTop }) => {
    const acf = reserva.acf;
    return (
        <div className="cartao-reserva">
            <Link className="cartao-reserva-link" to={`/reserva/${reserva.post_name}`} onClick={() => smoothScrollToTop()}>
                <div className="cartao-reserva-linha">
                    <span className="cartao-reserva-matricula">{acf.matricula}</span>
                    <span className="cartao-reserva-carro">{acf.marca} {nomeModelo(acf.modelo)}</span>
                    <EstadoReserva estado={acf.estadodareserva} />
                </div>
                <div className="cartao-reserva-cliente">
                    <span className="cartao-reserva-id">#{reserva.ID}</span> {acf.primeiro_nome} {acf.ultimo_nome}
                </div>
                <div className="cartao-reserva-datas">
                    <span className={destaque === 'entrada' ? 'cartao-reserva-data-destaque' : ''}>
                        <LoginOutlined /> {diaCurto(acf.dia_de_entrada)} {acf.horario_de_entrada}
                    </span>
                    <span className={destaque === 'saida' ? 'cartao-reserva-data-destaque' : ''}>
                        <LogoutOutlined /> {diaCurto(acf.dia_de_saida)} {acf.horario_de_saida}
                    </span>
                </div>
            </Link>
            {acf.telemovel && (
                <a className="cartao-reserva-telefone" href={`tel:${acf.telemovel}`} aria-label={`Ligar para ${acf.telemovel}`}>
                    <PhoneOutlined />
                </a>
            )}
        </div>
    );
};

const ListaCartoes = ({ reservas, destaque, smoothScrollToTop }) => {
    const [limite, setLimite] = useState(CARTOES_POR_PAGINA);

    if (reservas.length === 0) {
        return <p className="lista-reservas-vazia">Nenhuma reserva encontrada.</p>;
    }

    return (
        <div className="lista-cartoes-reservas">
            {reservas.slice(0, limite).map(reserva => (
                <CartaoReserva key={reserva.ID} reserva={reserva} destaque={destaque} smoothScrollToTop={smoothScrollToTop} />
            ))}
            {reservas.length > limite && (
                <Button className="lista-cartoes-mais" onClick={() => setLimite(limite + CARTOES_POR_PAGINA)}>
                    Mostrar mais ({reservas.length - limite})
                </Button>
            )}
        </div>
    );
};

const TabelaReservas = ({ reservas, smoothScrollToTop }) => {
    if (reservas.length === 0) {
        return <p>Nenhuma reserva encontrada.</p>;
    }

    return (
        <Table
            dataSource={reservas}
            rowKey={(reservation) => reservation.ID}
            pagination={{
                pageSize: 10,
                position: ["bottomCenter"],
            }}
        >
            <Table.Column
                title="ID"
                dataIndex="id"
                key="id"
                render={(ID, reservation) => (
                    <Link className="link-label" to={`/reserva/${reservation.post_name}`} onClick={() => smoothScrollToTop()}>
                        #{reservation.ID}
                    </Link>
                )}
            />
            <Table.Column
                title="Nome"
                key="nome"
                render={(reservation) => `${reservation.acf.primeiro_nome} ${reservation.acf.ultimo_nome}`}
            />
            <Table.Column
                title="Entrada"
                key="dataentrada"
                render={(reservation) => `${reservation.acf.dia_de_entrada} - ${reservation.acf.horario_de_entrada}`}
            />
            <Table.Column
                title="Saída"
                key="datasaida"
                render={(reservation) => `${reservation.acf.dia_de_saida} - ${reservation.acf.horario_de_saida}`}
            />
            <Table.Column
                title="Carro"
                key="marcaematricula"
                render={(reservation) => `${reservation.acf.marca} ${nomeModelo(reservation.acf.modelo)} | ${reservation.acf.matricula}`}
            />
            <Table.Column
                title="Estado"
                key="estado"
                render={(reservation) => <EstadoReserva estado={reservation.acf.estadodareserva} />}
            />
        </Table>
    );
};

// O Dashboard só carrega as reservas com entrada ou saída nos últimos dias (get_reservas_staff no WordPress)
const DIAS_CARREGADOS = 30;

// Resultado da pesquisa em todas as reservas: automática quando nos últimos 30 dias não há nenhuma, ou a pedido
const ResultadosServidor = ({ pesquisaServidor, conteudo, aPedido, onVoltar }) => {
    const voltar = aPedido && (
        <Button type="link" size="small" className="pesquisa-servidor-accao" onClick={onVoltar}>
            Voltar aos últimos {DIAS_CARREGADOS} dias
        </Button>
    );
    if (pesquisaServidor.aCarregar) {
        return (
            <div className="pesquisa-servidor-estado">
                <Spin size="small" /> {aPedido ? '' : `Sem resultados nos últimos ${DIAS_CARREGADOS} dias. `}A pesquisar em todas as reservas...
            </div>
        );
    }
    if (pesquisaServidor.erro) {
        return <p className="pesquisa-servidor-estado">Não foi possível pesquisar em todas as reservas. Tente novamente. {voltar}</p>;
    }
    if (pesquisaServidor.resultados.length === 0) {
        return <p className="lista-reservas-vazia">Nenhuma reserva encontrada, nem nas antigas. {voltar}</p>;
    }
    return (
        <>
            <p className="pesquisa-servidor-estado">
                Em todas as reservas ({pesquisaServidor.resultados.length}{pesquisaServidor.resultados.length >= 50 ? ', as 50 mais recentes' : ''})
                {voltar}
            </p>
            {conteudo(pesquisaServidor.resultados)}
        </>
    );
};

const TabDashboardAdmin = ({ isTabletMobile, searchFilterDay, searchFilterDaySaida, searchFilterSempre, searchQuery, handleInputChange, smoothScrollToTop, pesquisaServidor, pesquisaEmTodasAPedido, onPesquisarEmTodas, onVoltarAosUltimosDias }) => {

    const conteudo = (reservas, destaque) => (
        isTabletMobile
            ? <ListaCartoes reservas={reservas} destaque={destaque} smoothScrollToTop={smoothScrollToTop} />
            : <TabelaReservas reservas={reservas} smoothScrollToTop={smoothScrollToTop} />
    );

    return (
        <div className="dashboard-staff-lista">
            <div className="dashboard-staff-pesquisa">
                <SearchOutlined />
                <input
                    type="search"
                    className="input-geral"
                    placeholder="Pesquisar id, nome, matrícula, telefone ou data..."
                    value={searchQuery}
                    onChange={handleInputChange}
                />
            </div>
            {pesquisaServidor ? (
                <ResultadosServidor pesquisaServidor={pesquisaServidor} conteudo={conteudo} aPedido={pesquisaEmTodasAPedido} onVoltar={onVoltarAosUltimosDias} />
            ) : (
            <>
            {onPesquisarEmTodas && (
                <p className="pesquisa-servidor-estado">
                    A pesquisar nas reservas dos últimos {DIAS_CARREGADOS} dias.
                    <Button type="link" size="small" className="pesquisa-servidor-accao" onClick={onPesquisarEmTodas}>
                        Pesquisar em todas
                    </Button>
                </p>
            )}
            <Tabs
                className="dashboard-staff-tabs"
                items={[
                    { key: 'entradas', label: `Entradas hoje ${searchFilterDay.length}`, children: conteudo(searchFilterDay, 'entrada') },
                    { key: 'saidas', label: `Saídas hoje ${searchFilterDaySaida.length}`, children: conteudo(searchFilterDaySaida, 'saida') },
                    { key: 'todas', label: `Todas ${searchFilterSempre.length}`, children: conteudo(searchFilterSempre) },
                ]}
            />
            </>
            )}
        </div>
    );
};

export default TabDashboardAdmin;
